import { GridRowSelectionModel } from '@mui/x-data-grid';
import React, { Dispatch, SetStateAction, useState } from 'react';
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import { Button, Popper, Grow, Paper, ClickAwayListener, MenuList, MenuItem } from '@mui/material';
import { Stack, Box } from '@mui/system';
import { useTranslation } from 'react-i18next';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { deleteEvents } from 'src/shared/services/eventService';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';

type EventOverviewGridFooterButtonProps = {
  selectionModel: GridRowSelectionModel;
  fetchData: () => void;
  setSelectionModel: Dispatch<SetStateAction<GridRowSelectionModel>>;
};

export const EventOverviewGridFooterButton = (props: EventOverviewGridFooterButtonProps) => {
  const { selectionModel, fetchData, setSelectionModel } = props;
  const isMounted = useIsMounted();
  const { t } = useTranslation(['invoice', 'common']);

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [open, setOpen] = useState(false);

  const anchorRef = React.useRef<HTMLButtonElement>(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }
    setOpen(false);
  };

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = React.useRef(open);
  React.useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current?.focus();
    }
    prevOpen.current = open;
  }, [open]);

  const handleDeleteEvents = async () => {
    try {
      await deleteEvents(selectionModel as number[]);
      if (isMounted()) {
        setSelectionModel([]);
        fetchData();
      }
    } catch (error) {
      ConsoleHelper.log(error);
    }
  };

  return (
    <>
      <DeleteConfirmationDialog
        onClose={(val) => {
          if (val) {
            handleDeleteEvents();
          } else {
            setOpenDeleteDialog(false);
          }
        }}
        open={openDeleteDialog}
        title={selectionModel.length > 1 ? t('event:deleteEvents') : t('event:deleteEvent')}
        modaltext={t('common:irreversibleAction')}
        value=''
      />
      <Stack direction='row' spacing={2} sx={{ ml: 2 }}>
        <Box>
          <Button
            ref={anchorRef}
            id='composition-button'
            aria-controls={open ? 'composition-menu' : undefined}
            aria-expanded={open ? 'true' : undefined}
            aria-haspopup='true'
            onClick={() => {
              handleToggle();
            }}
            variant='contained'
            disableElevation
            endIcon={open ? <ArrowDropUp /> : <ArrowDropDown />}
          >
            {t('common:button.handle')}
          </Button>
          <Popper open={open} anchorEl={anchorRef.current} placement='bottom-start' transition disablePortal nonce={undefined}>
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{
                  transformOrigin: placement === 'bottom-start' ? 'left top' : 'left bottom',
                }}
              >
                <Paper>
                  <ClickAwayListener onClickAway={handleClose}>
                    <MenuList id='composition-menu' aria-labelledby='composition-button'>
                      <MenuItem onClick={() => setOpenDeleteDialog(true)}>
                        {selectionModel.length > 1 ? t('event:deleteEvents') : t('event:deleteEvent')}
                      </MenuItem>
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </Box>
      </Stack>
    </>
  );
};
